import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import SEO from '../components/SEO';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Firebase'e mesajı kaydet
      await addDoc(collection(db, 'messages'), {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        status: 'unread', // unread, read, replied
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset success message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      // Reset error message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'E-posta',
      value: 'info@aedoncommunity.com',
      description: '7/24 destek için bize yazın'
    },
    {
      icon: MapPin,
      title: 'Adres',
      value: 'İzmir, Türkiye',
      description: 'Sanal ofisimiz'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      value: '7/24 Online',
      description: 'Discord sunucumuzda aktif'
    }
  ];

  return (
    <>
      <SEO
        title="Iletisim - Aedon Community"
        description="Aedon Community ile iletişime geçin. Sorularınız, önerileriniz ve destek talepleriniz için bizimle iletişime geçebilirsiniz."
        keywords="iletişim, contact, aedon community, destek, yardım, soru"
        url="/contact"
      />
      
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Iletisim
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Sorularınız, önerileriniz ve destek talepleriniz için bizimle iletişime geçin
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* İletişim Bilgileri */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">İletişim Bilgileri</h2>
              
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex items-start space-x-4 p-6 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <info.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {info.title}
                    </h3>
                    <p className="text-blue-400 font-medium mb-1">
                      {info.value}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {info.description}
                    </p>
                  </div>
                </motion.div>
              ))}

              {/* Discord Bilgisi */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                className="p-6 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-lg border border-indigo-500/30"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <MessageSquare className="w-6 h-6 text-indigo-400" />
                  <h3 className="text-lg font-semibold text-white">Discord Sunucumuz</h3>
                </div>
                <p className="text-indigo-300 mb-2">
                  Anlık destek ve topluluk etkileşimi için
                </p>
                <a
                  href="https://discord.gg/aedonv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Discord'a Katıl
                </a>
              </motion.div>
            </motion.div>

            {/* İletişim Formu */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gray-800/50 p-8 rounded-lg border border-gray-700/50"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Bize Mesaj Gönderin</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Adınızı girin"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="E-posta adresinizi girin"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    Konu *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mesaj konusunu girin"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Mesaj *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Mesajınızı yazın..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Mesaj Gönder
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-900/30 border border-green-500/50 rounded-lg text-green-300 text-center"
                  >
                    ✅ Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300 text-center"
                  >
                    ❌ Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>

          {/* FAQ Bölümü */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-16"
          >
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Sık Sorulan Sorular
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  question: "Sunucuya nasıl katılabilirim?",
                  answer: "Discord sunucumuzdan FiveM sunucu bilgilerini alabilir ve oyuna katılabilirsiniz."
                },
                {
                  question: "Teknik destek nasıl alabilirim?",
                  answer: "Discord sunucumuzdaki destek kanallarından veya bu form üzerinden bizimle iletişime geçebilirsiniz."
                },
                {
                  question: "Sponsorluk için nasıl başvuru yapabilirim?",
                  answer: "Sponsorluk başvuruları için lütfen bizimle iletişime geçin, size detaylı bilgi vereceğiz."
                },
                {
                  question: "Topluluk kuralları nelerdir?",
                  answer: "Kurallar sayfamızdan detaylı topluluk kurallarını inceleyebilirsiniz."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.9 + index * 0.1 }}
                  className="p-6 bg-gray-800/30 rounded-lg border border-gray-700/30"
                >
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Contact;
